import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChapterContext } from "@/hooks/useChapterContext";
import { useMySubscriptions } from "@/hooks/useSubscription";
import type { ChapterRecord } from "@/lib/api-types";

/**
 * Central source of truth for frontend entitlement gating. This is
 * always paired with backend enforcement -- the signed-URL endpoint is
 * authoritative -- but the hook lets every viewer page render the
 * right CTA (sign in vs subscribe vs subscribe-for-class) without
 * re-deriving the same boolean lattice in six places.
 *
 * Returns an `access` discriminator the UI can switch on:
 *   - "loading"            : still figuring it out
 *   - "demo"               : open to everyone (chapter.is_demo === true)
 *   - "subscribed"         : authed AND has matching active subscription
 *   - "super_admin"        : authed AND role_id === SUPER_ADMIN (bypass)
 *   - "needs_auth"         : anonymous user on a premium chapter
 *   - "needs_onboarding"   : authed but no class_id picked yet
 *   - "needs_subscription" : authed, no active subscription
 *   - "wrong_class"        : authed, has sub but for a different class
 *   - "unknown"            : chapter context endpoint isn't shipped yet;
 *                            backend signed-URL is the source of truth
 *                            (renders the viewer optimistically)
 *
 * The frontend NEVER decides "yes you may stream this MP4" -- it only
 * decides "should I render the player, the paywall, or the sign-in
 * CTA". The signed-URL hook + 403/404 handling remain the final word.
 */

export type AccessStatus =
  | "loading"
  | "demo"
  | "subscribed"
  | "super_admin"
  | "needs_auth"
  | "needs_onboarding"
  | "needs_subscription"
  | "wrong_class"
  | "unknown";

export type ChapterAccess = {
  status: AccessStatus;
  /** True for any of the "OK to render the viewer" statuses. */
  allowed: boolean;
  /** Source chapter, populated once the curriculum endpoint resolves. */
  chapter: ChapterRecord | null;
  /** Class the user *needs* a subscription to (for the subscribe CTA). */
  needsClassId: string | null;
  /** Login next-param to use when bouncing anonymous users. */
  nextPath: string;
};

export function useChapterAccess(chapterId: string | undefined, currentPath: string): ChapterAccess {
  const { status: authStatus } = useAuth();
  const me = useCurrentUser();
  const mySubs = useMySubscriptions();
  const chapterCtx = useChapterContext(chapterId);

  const chapter = chapterCtx.data?.chapter ?? null;
  const activeSub = mySubs.data?.active ?? null;
  const nextPath = currentPath;

  // 1. Still loading any of the three sources -> "loading"
  if (authStatus === "loading") {
    return base("loading", chapter, null, nextPath);
  }
  if (authStatus === "authenticated" && me.isLoading) {
    return base("loading", chapter, null, nextPath);
  }
  if (authStatus === "authenticated" && mySubs.isLoading && !me.data?.active_subscription_id) {
    return base("loading", chapter, null, nextPath);
  }
  if (chapterCtx.isLoading) {
    return base("loading", chapter, null, nextPath);
  }

  // 2. SUPER_ADMIN bypass.
  if (authStatus === "authenticated" && me.data?.role_id === "SUPER_ADMIN") {
    return base("super_admin", chapter, null, nextPath);
  }

  // 3. Chapter context endpoint not yet shipped (it returns 404) ->
  // we can't authoritatively gate; let the viewer render and the
  // signed-URL endpoint decide. PaywallCard still appears on 403.
  if (chapter == null) {
    return base("unknown", chapter, null, nextPath);
  }

  // 4. Demo chapters are public -- anonymous + authed all welcome.
  if (chapter.is_demo === true) {
    return base("demo", chapter, null, nextPath);
  }

  // 5. Anonymous on a premium chapter -- needs sign-in.
  if (authStatus === "anonymous") {
    return base("needs_auth", chapter, chapter.class_id, nextPath);
  }

  // 6. Authed but no class_id yet -- they haven't completed onboarding.
  if (!me.data?.class_id) {
    return base("needs_onboarding", chapter, chapter.class_id, nextPath);
  }

  // 7. Use /subscriptions/me as the freshest frontend entitlement source.
  // /users/me can lag briefly after checkout because React Query may still
  // have a profile snapshot from before activation.
  if (activeSub) {
    if (activeSub.class !== chapter.class_id) {
      return base("wrong_class", chapter, chapter.class_id, nextPath);
    }
    return base("subscribed", chapter, chapter.class_id, nextPath);
  }

  if (mySubs.isSuccess) {
    return base("needs_subscription", chapter, chapter.class_id, nextPath);
  }

  // 8. If the subscription endpoint has not resolved, fall back to the user
  // pointer. The backend signed-URL endpoint remains authoritative.
  if (!me.data.active_subscription_id) {
    return base("needs_subscription", chapter, chapter.class_id, nextPath);
  }

  return base("subscribed", chapter, chapter.class_id, nextPath);
}

function base(
  status: AccessStatus,
  chapter: ChapterRecord | null,
  needsClassId: string | null,
  nextPath: string,
): ChapterAccess {
  const allowed =
    status === "demo" || status === "subscribed" || status === "super_admin" || status === "unknown";
  return { status, allowed, chapter, needsClassId, nextPath };
}
